import { zodResolver } from "@hookform/resolvers/zod";
import { Grid, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { useCreateActorVoiceMutation } from "Actors/Api/Mutations/useCreateActorVoiceMutation";
import { useExternalVoicesSearchQuery } from "Actors/Api/Queries/useExternalVoicesSearchQuery";
import {
  type CreateVoiceFormData,
  createVoiceFormSchema,
} from "Actors/Forms/voiceFormSchema";
import { BlFormDropdown } from "Forms/Components/BlFormDropdown";
import { BlFormInput } from "Forms/Components/BlFormInput";
import {
  getLanguageOptions,
  getVoiceLlmOptions,
} from "Forms/Utils/EnumOptions";
import { LanguageCodes, VoiceLlm } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlModal } from "Shared/Dialogs/BlModal";
import { BlAutocomplete } from "Shared/Inputs/BlAutocomplete";
import type { BlOption } from "Shared/Inputs/BlOption";

interface CreateActorVoiceModalProps {
  isOpen: boolean;
  actorID: string;
  onClose: () => void;
  onCreated: (actorVoiceID: string) => void;
}

export const CreateActorVoiceModal = ({
  isOpen,
  actorID,
  onClose,
  onCreated,
}: CreateActorVoiceModalProps): React.ReactNode => {
  const { t } = useResource();

  const [externalVoiceSearchQuery, setExternalVoiceSearchQuery] = useState("");
  const [selectedExternalVoice, setSelectedExternalVoice] =
    useState<BlOption | null>(null);
  // Set when the chosen voice is tied to a single language (e.g. AIVoice catalog voices) — the language
  // dropdown is then locked to it so the user cannot pick a language the voice does not support.
  const [voiceLanguage, setVoiceLanguage] = useState<LanguageCodes | null>(
    null,
  );

  const createMutation = useCreateActorVoiceMutation(actorID);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateVoiceFormData>({
    resolver: zodResolver(createVoiceFormSchema),
    defaultValues: {
      name: "",
      tags: "",
      voiceLlm: VoiceLlm.ElevenLabs_V3,
      language: LanguageCodes.Czech,
      externalVoiceID: "",
      externalEndpoint: "",
    },
  });

  const selectedVoiceLlm = watch("voiceLlm");

  const { data: externalVoicesData, isLoading: isSearchingVoices } =
    useExternalVoicesSearchQuery({
      voiceLlm: selectedVoiceLlm,
      searchQuery: externalVoiceSearchQuery || undefined,
      limit: 20,
    });

  const externalVoiceOptions: BlOption[] = (
    externalVoicesData?.items ?? []
  ).map((item) => ({ value: item.externalID, label: item.name }));

  // Reset autocomplete when voiceLlm changes
  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name === "voiceLlm") {
        setSelectedExternalVoice(null);
        setExternalVoiceSearchQuery("");
        setValue("externalVoiceID", "");
        setVoiceLanguage(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  useEffect(() => {
    if (!isOpen) {
      reset();
      setSelectedExternalVoice(null);
      setExternalVoiceSearchQuery("");
      setVoiceLanguage(null);
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: CreateVoiceFormData) => {
    try {
      const result = await createMutation.mutateAsync({
        name: data.name,
        tags: data.tags || undefined,
        voiceLlm: data.voiceLlm,
        language: data.language,
        externalVoiceID: data.externalVoiceID || undefined,
        externalEndpoint: data.externalEndpoint || undefined,
      });

      toast.success(
        t(Resources.Actors.ActorDetail.NewVoiceModal.SuccessMessage),
      );
      reset();
      setSelectedExternalVoice(null);

      if (result.actorVoiceID) {
        onCreated(result.actorVoiceID);
      } else {
        onClose();
      }
    } catch {
      // error handled by mutation
    }
  };

  const handleClose = () => {
    reset();
    setSelectedExternalVoice(null);
    setExternalVoiceSearchQuery("");
    onClose();
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Actors.ActorDetail.NewVoiceModal.Title)}
      onCancel={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Actors.ActorDetail.NewVoiceModal.Create)}
      maxWidth="700px"
    >
      <Stack spacing={Spacing.S}>
        <Grid container spacing={Spacing.S}>
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="name"
              label={t(Resources.Actors.ActorDetail.VoiceName)}
              errors={errors}
              required
            />
          </Grid>
          <Grid size={6}>
            <BlFormInput
              control={control}
              name="tags"
              label={t(Resources.Actors.ActorDetail.Tags)}
              errors={errors}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t(Resources.Actors.ActorDetail.VoiceTraining)}
        </Typography>

        <Grid container spacing={Spacing.S}>
          <Grid size={6}>
            <BlFormDropdown
              control={control}
              name="voiceLlm"
              label={t(Resources.Actors.ActorDetail.TrainingModel)}
              options={getVoiceLlmOptions()}
              errors={errors}
              required
            />
          </Grid>
          <Grid size={6}>
            <BlFormDropdown
              control={control}
              name="language"
              label={t(Resources.Actors.ActorDetail.NativeLanguage)}
              options={getLanguageOptions()}
              errors={errors}
              required
              // Catalog voices (e.g. AIVoice) are tied to one language — lock the field to it.
              disabled={voiceLanguage !== null}
            />
          </Grid>
          <Grid size={12}>
            <BlFormInput
              control={control}
              name="externalEndpoint"
              label={t(Resources.Actors.ActorDetail.ModelID)}
              errors={errors}
            />
          </Grid>
          <Grid size={12}>
            <BlAutocomplete
              options={externalVoiceOptions}
              isLoading={isSearchingVoices}
              onSearch={setExternalVoiceSearchQuery}
              value={selectedExternalVoice}
              onOptionSelected={(item) => {
                setSelectedExternalVoice(item);
                const externalVoiceID = item?.value?.toString() ?? "";
                setValue("externalVoiceID", externalVoiceID);

                // Single-language voices (AIVoice catalog) dictate the language — set and lock it.
                const selectedItem = externalVoicesData?.items.find(
                  (i) => i.externalID === externalVoiceID,
                );
                const itemLanguage = selectedItem?.language ?? null;
                setVoiceLanguage(itemLanguage);
                if (itemLanguage) {
                  setValue("language", itemLanguage);
                }
              }}
              labelProps={{
                label: t(Resources.Actors.ActorDetail.ExternalVoiceID),
                required: true,
              }}
              placeholder={t(Resources.Actors.ActorDetail.SearchExternalVoice)}
              inputName="externalVoiceID"
              fieldError={errors.externalVoiceID}
            />
          </Grid>
        </Grid>
      </Stack>
    </BlModal>
  );
};
